import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import Modal from "@mui/material/Modal";
import { useWallet } from "@solana/wallet-adapter-react";
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { VersionedTransaction } from "@solana/web3.js";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { createTransactionWithNFT, submitTransaction } from "../api/bot.api";
import { getConnection } from "../libs/utils";
import { setSnackMsgAction } from "../store/slice";
import NumberField from "./NumberField";
import { NftInterface } from "./types";
import { Typography } from "@mui/material";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "10px",
};

export type AddModalProps = {
    open: boolean;
    onClose: () => void;
    nft: NftInterface;
};

export default function AddAmountModal({ open, onClose, nft }: AddModalProps) {
    const { connected, publicKey, sendTransaction } = useWallet();
    const { setVisible } = useWalletModal();
    const settings = useSelector((state: any) => state.settings);
    const [isAdding, setIsAdding] = React.useState(false);
    const [amount, setAmount] = React.useState("");
    const [errors, setErrors] = React.useState({amount: ""});
    const dispatch = useDispatch();

    const handleClose = () => {
        onClose();
    };

    const handleAdd = async () => {
        if (!connected || !publicKey) {
            setVisible(true);
            return;
        }
        // Proceed with the signup process
        if (!amount) {
            setErrors({...errors, amount: "Please input amount in USDC"});
            return;
        }
        if (amount < settings.minSignupAmount) {
            setErrors({...errors, amount: `Amount must be at least ${settings.minSignupAmount}`});
            return;
        }
        setIsAdding(true);
        const res = await createTransactionWithNFT({
            signupAddress: nft.referrerSignupAddress || settings.rootSignupAddress,
            amount,
            investorPublicKey: publicKey?.toBase58(),
            nftMintPublicKey: nft.id,
        });
        setIsAdding(false);
        if (!res.success) {
            toast.error(res.msg);
            return;
        }
        const { signedTx, historyId } = res.data;
        if (!signedTx || !historyId) {
            toast.error("Failed to create transaction");
            return;
        }
        // Deserialize the transaction
        const tx = VersionedTransaction.deserialize(
            new Uint8Array(Buffer.from(signedTx, "base64"))
        );
        // Sign the transaction
        if (!sendTransaction) {
            toast.error("Failed to sign transaction");
            return;
        }
        const signature = await sendTransaction(tx, getConnection());
        if (!signature) {
            toast.error("Failed to sign transaction");
            return;
        }
        console.log(`signature: ${signature}`);
        // Send the signed transaction to the backend
        dispatch(setSnackMsgAction("Transaction submitted. Please wait for confirmation."));
        const submitRes = await submitTransaction({
            signature,
            historyId,
        });
        dispatch(setSnackMsgAction(""));
        if (!submitRes.success) {
            toast.error(submitRes.msg);
            return;
        }
        toast.success("Transaction submitted successfully");
        handleClose();
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={{ ...style, display: "flex", flexDirection: "column", alignItems: "center", width: "95vw", maxWidth: "600px", px: {xs: 1, sm: 4}, pt: {xs: 6, sm: 6}, pb: {xs: 4, sm: 6} }}>
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", mb: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>Add Amount</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>Add USDC amount to your existing NFT</Typography>
                </Box>
                <Grid container spacing={2} sx={{ width: "100%" }}>    
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <NumberField
                            label={`USDC Amount (Min: ${settings.minSignupAmount})`}
                            slotProps={{ htmlInput: { min: settings.minSignupAmount } }}
                            sx={{ width: "100%" }}
                            value={amount}
                            onChange={(value) => {setAmount(value); setErrors({amount: ""});}}
                            helperText={errors.amount}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }} sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            variant="contained"
                            onClick={handleAdd}
                            sx={{ minWidth: "150px", height: "40px" }}
                            loading={isAdding}
                            loadingIndicator="Adding…"
                            disabled={!!errors.amount}
                        >
                            Add Amount
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Modal>
    );
}
